/**
 * PayeeCombobox — typeahead/combobox for selecting a Payee.
 *
 * - Shows all payees on first focus
 * - Filters by name as the user types (case-insensitive substring match)
 * - Arrow keys navigate the list; Enter / click selects; Escape closes
 * - Click outside closes without changing the selection
 * - Clears the selected payee_id when the user erases the input text
 */

import { useEffect, useRef, useState } from 'react';
import type { Payee } from '../types';

interface Props {
  payees: Payee[];
  value: string;          // currently selected payee_id (as string) or ''
  onChange: (payeeId: string) => void;
  disabled?: boolean;
}

export default function PayeeCombobox({ payees, value, onChange, disabled }: Props) {
  const selectedPayee = payees.find((p) => String(p.payee_id) === value) ?? null;

  const [inputText, setInputText]   = useState(selectedPayee?.payee_name ?? '');
  const [open, setOpen]             = useState(false);
  const [highlighted, setHighlighted] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef     = useRef<HTMLInputElement>(null);
  const listRef      = useRef<HTMLUListElement>(null);

  // Keep input text in sync if the parent resets value to ''
  useEffect(() => {
    if (value === '') setInputText('');
  }, [value]);

  const filtered = inputText.trim() === ''
    ? payees
    : payees.filter((p) =>
        p.payee_name.toLowerCase().includes(inputText.toLowerCase()),
      );

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeAndRestore();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  });

  // Scroll highlighted item into view
  useEffect(() => {
    if (!listRef.current) return;
    const item = listRef.current.children[highlighted] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [highlighted]);

  function selectPayee(p: Payee) {
    onChange(String(p.payee_id));
    setInputText(p.payee_name);
    setOpen(false);
    setHighlighted(0);
  }

  /** If the user clicks away without choosing, restore the last confirmed name. */
  function closeAndRestore() {
    setOpen(false);
    setHighlighted(0);
    if (value) {
      setInputText(selectedPayee?.payee_name ?? '');
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInputText(e.target.value);
    setHighlighted(0);
    setOpen(true);
    // Clear the selection if user is editing after having chosen one
    if (value) onChange('');
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setOpen(true);
        return;
      }
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlighted((h) => Math.max(h - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filtered[highlighted]) selectPayee(filtered[highlighted]);
        break;
      case 'Escape':
        closeAndRestore();
        break;
      case 'Tab':
        // Let Tab move focus; close the dropdown cleanly
        closeAndRestore();
        break;
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={inputText}
        onChange={handleInputChange}
        onFocus={() => { setOpen(true); setHighlighted(0); }}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder="Type to search payees…"
        autoComplete="off"
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
      />

      {open && !disabled && filtered.length > 0 && (
        <ul
          ref={listRef}
          className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto text-sm"
          role="listbox"
        >
          {filtered.map((p, i) => (
            <li
              key={p.payee_id}
              role="option"
              aria-selected={String(p.payee_id) === value}
              onMouseDown={(e) => {
                // mousedown fires before blur — prevent input losing focus before we register the click
                e.preventDefault();
                selectPayee(p);
              }}
              onMouseEnter={() => setHighlighted(i)}
              className={`px-3 py-2 cursor-pointer ${
                i === highlighted ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
              } ${String(p.payee_id) === value ? 'font-medium' : ''}`}
            >
              {p.payee_name}
            </li>
          ))}
        </ul>
      )}

      {open && !disabled && filtered.length === 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm text-gray-400">
          No payees match "{inputText}"
        </div>
      )}
    </div>
  );
}
